import { memo } from "react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"

/**
 * InlineMarkdown renders a *single line* of markdown: emphasis (bold/italic),
 * inline code, links, and strikethrough. Block-level constructs (headings,
 * lists, paragraphs, code blocks, blockquotes) are flattened to inline spans so
 * the result stays on one line — suitable for compact UI like dropdown items,
 * select triggers, table cells, and badges.
 */
export const InlineMarkdown = memo(function InlineMarkdown({
  children,
  className,
}: {
  children: string
  className?: string
}) {
  // Collapse newlines so multi-line titles don't force block layout.
  const value = (children || "").replace(/\s*\n+\s*/g, " ").trim()

  return (
    <span className={className} dir="auto">
      <Markdown
        // Everything maps to inline elements; block wrappers become plain spans.
        components={{
          p: ({ node, ...rest }) => <span {...rest} />,
          h1: ({ node, ...rest }) => <span {...rest} />,
          h2: ({ node, ...rest }) => <span {...rest} />,
          h3: ({ node, ...rest }) => <span {...rest} />,
          h4: ({ node, ...rest }) => <span {...rest} />,
          h5: ({ node, ...rest }) => <span {...rest} />,
          h6: ({ node, ...rest }) => <span {...rest} />,
          ul: ({ node, ...rest }) => <span {...rest} />,
          ol: ({ node, ...rest }) => <span {...rest} />,
          li: ({ node, ...rest }) => <span {...rest} />,
          blockquote: ({ node, ...rest }) => <span {...rest} />,
          pre: ({ node, ...rest }) => <span {...rest} />,
          hr: () => null,
          br: () => <span> </span>,
          a: ({ node, ...rest }) => <a className="text-blue-600 hover:underline" {...rest} />,
          strong: ({ node, ...rest }) => <strong className="font-semibold" {...rest} />,
          em: ({ node, ...rest }) => <em className="italic" {...rest} />,
          del: ({ node, ...rest }) => <del className="line-through" {...rest} />,
          code: ({ node, ...rest }) => (
            <code className="bg-gray-100 px-1 py-0.5 rounded text-[0.9em] font-mono" {...rest} />
          ),
          // Images make no sense inline in a dropdown; drop them.
          img: () => null,
        }}
        remarkPlugins={[remarkGfm]}
      >
        {value}
      </Markdown>
    </span>
  )
})
